import React from 'react';
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  NonIndexRouteObject,
  IndexRouteObject,
} from 'react-router-dom';
import { AnyObject } from '@triare/auth-redux';
import { UserOutlined } from '@ant-design/icons';
import Home from '../pages/Home';
import { paramsToString, routes as menuRoutes } from '../components/Layout/Menu';
import PrivateRoute from './PrivateRoute';
import { AuthUser as User } from '../store/auth';
import { UserRoles } from '../enums/user';
import SignIn from '../pages/Auth/SignIn';
import ForgotPassword from '../pages/Auth/ForgotPassword';
import ResetPassword from '../pages/Auth/ResetPassword';
import ConfirmEmail from '../pages/Auth/ConfirmEmail';
import UnsavedChangesProvider from '../context/unsavedChanges';

export function generateDefaultUrl(user?: User | null) {
  return `${paramsToString(
    user,
    user && user?.role === UserRoles.ADMIN ? undefined : getRouteParams('/', user),
  )}`;
}

export interface RouteElements {
  [role: string]: React.ReactNode | null;
}

interface RouteObjectExtension {
  elements?: RouteElements;
  params?: ((user: User) => string) | AnyObject;
}

interface MyIndexRouteObject extends IndexRouteObject, RouteObjectExtension {
}

interface MyNonIndexRouteObject extends NonIndexRouteObject, RouteObjectExtension {
}

export type RouteObject = MyIndexRouteObject | MyNonIndexRouteObject;
export type RouteRoles = (string | ((user: User) => boolean))[];

export interface Route {
  route?: RouteObject;
  roles?: RouteRoles;
  name?: string | ((user: User) => string);
  icon?: React.ReactNode | ((user: User) => React.ReactNode);
  parent?: Route;
  hidden?: boolean;
  onClick?: () => void;
  children?: Route[];
  outlet?: true;
  roleNeeded?: 'user' | 'root' | 'admin';
  privateRoute?: boolean;
  disabled?: string | ((user: User) => boolean);
  align?: 'start' | 'end'; // 'left' | 'right';

  // eslint-disable-next-line
  [key: string]: any;
}

export function addChildrenRoutes(routes: Route[]): Route[] {
  const list: Route[] = [];

  const recursiveUpdate = (item: Route, parent?: Route): Route => {
    if (parent) {
      // eslint-disable-next-line no-param-reassign
      item.parent = parent;

      if (parent?.route?.children && item.route) {
        parent.route.children.push(item.route);
      }

      if (!item.outlet && item.route) {
        list.push(item);

        if (parent.route) {
          const childList = (parent.route?.children || []).filter((child) => child !== item.route);

          if (childList.length) {
            Object.assign(parent.route, {
              children: childList,
            });
          } else {
            // eslint-disable-next-line no-param-reassign
            delete parent.route.children;
          }
        }

        Object.assign(item.route, {
          path: `${parent.route?.path}/${item.route?.path}`.replace(/\/+/, '/').replace(/^\/+/, ''),
        });
      }
    }

    if (item.route) {
      const { route } = item;

      if (item.privateRoute && (route?.element || route?.elements)) {
        route.element = <PrivateRoute element={route?.element} elements={route?.elements} item={item} />;
      }

      if (item.children) {
        // eslint-disable-next-line no-param-reassign
        route.children = [];

        item.children.forEach((childRoute) => recursiveUpdate(childRoute, item));
      }
    } else if (item.children) {
      item.children.filter(({ route }) => route).forEach((child) => list.push(recursiveUpdate(child)));
    }

    return item;
  };

  return [...routes.map((route) => recursiveUpdate(route)), ...list].filter(({ route }) => route);
}

export const routes: Route[] = addChildrenRoutes([
  {
    privateRoute: true,
    name: 'Home',
    route: {
      path: '/',
      element: <Navigate to="/users" replace />,
    },
    children: [
      {
        name: 'Sign In',
        icon: <UserOutlined style={{ fontSize: 18 }} />,
        route: {
          path: 'sign-in',
          element: <SignIn />,
        },
      },
      {
        name: 'Forgot password',
        route: {
          path: 'forgot-password',
          element: <ForgotPassword />,
        },
      },
      {
        name: 'Reset password',
        route: {
          path: 'reset-password/:secretKey',
          element: <ResetPassword />,
        },
      },
      {
        name: 'Confirm email',
        route: {
          path: 'confirm-email/:secretKey',
          element: <ConfirmEmail />,
        },
      },
      ...menuRoutes,
    ],
  },
]);

const router = createBrowserRouter([
  ...routes.map(({ route }) => ({
    ...route,
    element: <UnsavedChangesProvider>{route?.element}</UnsavedChangesProvider>,
  })),
  {
    path: '*',
    element: <Navigate to="/users" replace />,
  },
]);

export function getRouteParams(path: string, user?: User | null): AnyObject {
  let params: ((user: User) => string) | AnyObject = {};

  routes.find(({ route }) => {
    if (route && route.path && route.path === path) {
      if (route.params) {
        params = route.params;
      }

      return true;
    }

    return false;
  });

  if (typeof params === 'function') {
    if (user) {
      return params(user);
    }

    return {};
  }

  return params;
}

export default function Routes(): React.ReactNode {
  return <RouterProvider router={router} />;
}
